import React, { useEffect, useState } from "react";
import { Download, X, Sparkles } from "lucide-react";

/**
 * UpdateBanner
 * Sticky, persistent banner that appears when a new Service Worker version is
 * waiting to activate. Key behaviours:
 *  - Persists across reloads via localStorage until the user taps AGGIORNA
 *    (or the current SW version matches the "seen" one in storage).
 *  - Large, high-contrast, with a pulsing gold glow so it is impossible to miss.
 *  - Detects new versions by 1) SW `updatefound` events (dispatched from
 *    index.html) and 2) polling /sw.js text and comparing the CACHE version
 *    string with the last one applied.
 */
const STORE_KEY_SEEN = "guzz_sw_version_seen";      // last version user has been notified about
const STORE_KEY_APPLIED = "guzz_sw_version_applied"; // last version user clicked "AGGIORNA" on

const parseCacheVersion = (swText) => {
  const m = /CACHE\s*=\s*["']([^"']+)["']/.exec(swText || "");
  return m ? m[1] : null;
};

export const UpdateBanner = () => {
  const [waitingSW, setWaitingSW] = useState(null);
  const [remoteVersion, setRemoteVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onUpdate = (e) => {
      const sw = e?.detail?.waiting || window.__SW_WAITING__ || null;
      if (sw) setWaitingSW(sw);
    };
    window.addEventListener("sw-update-available", onUpdate);
    if (window.__SW_WAITING__) setWaitingSW(window.__SW_WAITING__);

    // Poll /sw.js text so we detect new versions even if SW events race.
    const checkVersion = async () => {
      try {
        const res = await fetch("/sw.js", { cache: "no-cache" });
        const txt = await res.text();
        const v = parseCacheVersion(txt);
        if (v) setRemoteVersion(v);
      } catch (_) {}
    };
    checkVersion();
    const t = setInterval(checkVersion, 60_000);
    const onFocus = () => checkVersion();
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("sw-update-available", onUpdate);
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
    };
  }, []);

  // Compute whether we should show the banner based on SW event OR version diff
  const applied = typeof window !== "undefined" ? localStorage.getItem(STORE_KEY_APPLIED) : null;
  const versionDiffers = remoteVersion && remoteVersion !== applied;
  const shouldShow = !dismissed && (waitingSW || versionDiffers);

  useEffect(() => {
    if (remoteVersion && shouldShow) {
      try { localStorage.setItem(STORE_KEY_SEEN, remoteVersion); } catch (_) {}
    }
  }, [remoteVersion, shouldShow]);

  const applyUpdate = () => {
    try {
      if (remoteVersion) localStorage.setItem(STORE_KEY_APPLIED, remoteVersion);
    } catch (_) {}
    try {
      if (waitingSW && waitingSW.postMessage) {
        waitingSW.postMessage("SKIP_WAITING");
      } else if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistration().then((reg) => {
          if (reg && reg.waiting) reg.waiting.postMessage("SKIP_WAITING");
          // Also request update to force pull the new SW if we detected via polling
          if (reg) reg.update().catch(() => {});
        });
      }
      setTimeout(() => window.location.reload(), 600);
    } catch (_) {
      window.location.reload();
    }
  };

  if (!shouldShow) return null;

  return (
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[9999] w-[96%] max-w-xl update-banner-pulse"
      data-testid="update-banner"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-center gap-4 px-5 py-4 bg-midnight border-2 border-gold shadow-2xl rounded-sm">
        <div className="h-12 w-12 rounded-full bg-gold flex items-center justify-center shrink-0 relative">
          <Sparkles size={22} className="text-midnight" strokeWidth={2.5} />
          <span className="absolute inset-0 rounded-full ring-2 ring-gold/60 animate-ping" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-base sm:text-lg font-bold leading-tight">
            Nuova versione disponibile
          </p>
          <p className="text-white/80 text-xs sm:text-sm leading-tight mt-1">
            Tocca <span className="text-gold font-semibold">AGGIORNA</span> per vedere le ultime novità di Guzzardella Auto
          </p>
        </div>
        <button
          type="button"
          onClick={applyUpdate}
          className="px-4 py-3 bg-gold text-midnight text-sm font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors rounded-sm shrink-0 shadow-lg flex items-center gap-2"
          data-testid="update-banner-apply-btn"
        >
          <Download size={16} strokeWidth={3} />
          Aggiorna
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Chiudi (riapparirà al prossimo accesso)"
          className="h-8 w-8 flex items-center justify-center text-white/50 hover:text-white transition-colors shrink-0"
          data-testid="update-banner-dismiss-btn"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default UpdateBanner;
